import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import Hero from './components/Sections/Hero';
import Approach from './components/Sections/Approach';
import Quote from './components/Sections/Quote';
import AskMe from './components/Sections/AskMe';
import ScrollProgress from './components/UI/ScrollProgress';
import WhyMe from './components/Sections/WhyMe';
import Skills from './components/Sections/Skills';
import Projects from './components/Sections/Projects';
import Stats from './components/Sections/Stats';
import Experience from './components/Sections/Experience';
import Certifications from './components/Sections/Certifications';
import About from './components/Sections/About';
import ProjectModal from './components/UI/ProjectModal';
import CustomCursor from './components/UI/CustomCursor';
import Preloader from './components/UI/Preloader';
import SmoothScroll, { useLenis } from './components/UI/SmoothScroll';
import { Project } from './types';

// Types for View Navigation
type ViewType = 'home' | 'about' | 'experience' | 'projects' | 'certifications';

// Full Page View Component — kept outside AppContent to prevent remounting
interface FullPageViewProps {
  children: React.ReactNode;
  title: string;
  onClose: () => void;
}

const FullPageView: React.FC<FullPageViewProps> = ({ children, title, onClose }) => {
  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.99, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.99, y: 8 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className='modal-scroll-container fixed inset-0 z-50 bg-paper overflow-y-auto overscroll-contain'
      onWheel={handleWheel}
      onTouchMove={handleTouchMove}
    >
      <div className='max-w-7xl mx-auto px-5 sm:px-6 py-6 md:py-10'>
        <div className='flex justify-between items-center mb-10 sticky top-0 bg-paper/90 backdrop-blur-md py-4 z-30 border-b border-line'>
          <h2 className='font-medium tracking-tight text-2xl sm:text-3xl text-ink'>{title}</h2>
          <button
            onClick={onClose}
            data-cursor='pointer'
            className='w-11 h-11 rounded-full border border-line flex items-center justify-center text-ink hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300'
            aria-label='Close'
          >
            <X size={20} />
          </button>
        </div>
        <div className='pb-20'>{children}</div>
      </div>
    </motion.div>
  );
};

// Inner component that has access to Lenis context
const AppContent: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentView, setCurrentView] = useState<ViewType>('home');

  const { setLocked } = useLenis();

  // Dark mode state — persisted in localStorage, defaults to system preference
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const toggleDark = () => setIsDark(prev => !prev);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Lock scroll when FullPageView is open
  useEffect(() => {
    if (currentView !== 'home') {
      setLocked(true);
      return () => {
        setLocked(false);
      };
    }
  }, [currentView, setLocked]);

  const handleNavigate = (view: ViewType) => {
    setCurrentView(view);
    if (view === 'home') {
      window.scrollTo(0, 0);
    }
  };

  const handleCloseFullPage = () => {
    setCurrentView('home');
  };

  return (
    <div className='min-h-screen relative bg-paper text-ink font-sans'>
      <Preloader />
      <div className='grain' aria-hidden='true' />
      <CustomCursor />
      <ScrollProgress />
      <Navbar isDark={isDark} onToggleDark={toggleDark} />

      <main>
        <Hero />
        <div className='space-y-24 sm:space-y-36 mt-24 sm:mt-36'>
          <Approach />
          <Quote />
          <WhyMe />
          <Skills />
          <AskMe />
          <Projects onProjectSelect={setSelectedProject} />
          <Stats />
          <Experience />
          <Certifications />
        </div>
      </main>

      <Footer onNavigate={handleNavigate} />

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {currentView !== 'home' && (
          <FullPageView
            title={
              currentView === 'about'
                ? 'About Me'
                : currentView.charAt(0).toUpperCase() + currentView.slice(1)
            }
            onClose={handleCloseFullPage}
          >
            {currentView === 'about' && <About />}
            {currentView === 'experience' && <Experience isDetailed={true} />}
            {currentView === 'projects' && (
              <Projects onProjectSelect={setSelectedProject} isDetailed={true} />
            )}
            {currentView === 'certifications' && <Certifications isDetailed={true} />}
          </FullPageView>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main App component that wraps with SmoothScroll provider
const App: React.FC = () => {
  return (
    <SmoothScroll>
      <AppContent />
    </SmoothScroll>
  );
};

export default App;
